import { isEqual } from 'lodash';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UpdateScratchpadBucketInput,
  useGetScratchpadBucketQuery,
  useUpdateScratchpadBucketMutation,
} from '../../api/graphql/generated';
import { sanitizeObject } from '../../util/type-coercion';
import { useConfirmActionAlert } from '../alert/confirmation-alert';

interface UseScratchpadBucketChangesOptions<T> {
  bucketId?: string;
  // Function to extract comparable data from bucket.data, if not provided uses entire bucket.data
  extractBucketData?: (bucketData: unknown) => T;
  // Optional comparison function, defaults to lodash isEqual
  compareValues?: (current: T, bucket: T) => boolean;
  // Whether to sanitize data for reliable comparison (normalize types and structure)
  normalizeData?: boolean;
}

interface CheckChangesResult {
  hasChanges: boolean;
  isLoading: boolean;
}

interface WithScratchpadBucketChangeCheckOptions {
  skipConfirmation?: boolean;
  confirmationHeader?: string;
  confirmationMessage?: string;
  confirmText?: string;
  cancelText?: string;
  onCancel?: () => void;
  updateBucketOnConfirm?: boolean;
}

export const useScratchpadBucketChanges = <T extends Record<string, unknown>>({
  bucketId,
  extractBucketData,
  compareValues = isEqual,
  normalizeData = false,
}: UseScratchpadBucketChangesOptions<T>) => {
  const { i18n } = useTranslation();
  const confirmActionAlert = useConfirmActionAlert();
  const [updateScratchpadBucket] = useUpdateScratchpadBucketMutation();

  const { data: bucketData, loading } = useGetScratchpadBucketQuery({
    variables: { input: { id: bucketId || '' } },
    skip: !bucketId,
  });

  const bucket = bucketData?.getScratchpadBucket;

  const checkForChanges = useCallback(
    (currentValues?: T): CheckChangesResult => {
      if (!bucketId || !currentValues || !bucket?.data) {
        return { hasChanges: false, isLoading: loading };
      }

      let bucketValues = extractBucketData
        ? extractBucketData(bucket.data)
        : (bucket.data as T);

      let normalizedCurrentValues = currentValues;

      // Apply data sanitization if requested
      if (normalizeData) {
        normalizedCurrentValues = sanitizeObject(currentValues) as T;
        bucketValues = sanitizeObject(bucketValues) as T;
      }

      const hasChanges = !compareValues(normalizedCurrentValues, bucketValues);

      return { hasChanges, isLoading: loading };
    },
    [bucketId, bucket, loading, extractBucketData, compareValues, normalizeData]
  );

  const withScratchpadBucketChangeCheck = useCallback(
    async <R>(
      currentValues: T,
      action: (newVersion?: number) => Promise<R>,
      options?: WithScratchpadBucketChangeCheckOptions
    ): Promise<R> => {
      const { hasChanges, isLoading } = checkForChanges(currentValues);

      if (hasChanges && !isLoading && !options?.skipConfirmation) {
        return new Promise((resolve, reject) => {
          const confirmUpdate = confirmActionAlert({
            header:
              options?.confirmationHeader ||
              i18n.t('common:update_confirmation'),
            message:
              options?.confirmationMessage ||
              i18n.t('common:update_confirmation_message', {
                name: bucket?.name || i18n.t('common:untitled'),
              }),
            confirmText: options?.confirmText,
            cancelText: options?.cancelText,
            confirmationAction: async () => {
              try {
                let newVersion: number | undefined;

                // If updateBucketOnConfirm is true and we have a bucket,
                // update the bucket data while preserving other fields
                if (options?.updateBucketOnConfirm && bucket) {
                  const input: UpdateScratchpadBucketInput = {
                    id: bucket.id,
                    active: bucket.active,
                    data: currentValues,
                    name: bucket.name,
                    scope: bucket.scope,
                    parentId: bucket.parentId,
                    tags: bucket.tags,
                    notes: bucket.notes,
                    type: bucket.type,
                  };

                  const result = await updateScratchpadBucket({
                    variables: { input },
                  });

                  newVersion = result.data?.updateScratchpadBucket?.version;
                }

                // Then execute the original action with the new version
                const result = await action(newVersion);
                resolve(result);
              } catch (error) {
                reject(error);
              }
            },
            onCancel: options?.onCancel,
          });
          confirmUpdate();
        });
      }

      return action();
    },
    [checkForChanges, confirmActionAlert, i18n, bucket, updateScratchpadBucket]
  );

  return {
    withScratchpadBucketChangeCheck,
    bucket,
    isLoading: loading,
  };
};
